import { themeGet } from '@styled-system/theme-get';
import React from 'react';
import { useSetRecoilState } from 'recoil';
import styled, { css } from 'styled-components';

import { Box } from '@/components/box';
import { Flex } from '@/components/flex';
import { Icon } from '@/components/icon';
import { RouteTooltip } from '@/components/route-tooltip';
import { Tooltip } from '@/components/tooltip';
import { TrafficLight } from '@/components/traffic-light';
import { DELAY_STATUS, MODE_ROAD, MODE_TRANSFER, MODE_WATER, TRANSFER_STOP } from '@/lib/constants';
import { DATA_IMAGES } from '@/lib/constants/styles';
import { activeMapSegmentState } from '@/state/map';
import { RouteScheduleSegmentInterface, RouteSegmentInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { convertModeType } from '@/utils/format';
import { media } from '@/utils/media';

import { segmentModeVariants } from './theme';

// Determines if Segment is Transfer
const checkIfSegmentTransfer = (segment: RouteSegmentInterface | RouteScheduleSegmentInterface) =>
  segment.mode === MODE_TRANSFER && 'transferType' in segment && segment.transferType === MODE_TRANSFER;

// Determines if Segment is Stop
const checkIfSegmentStop = (segment: RouteSegmentInterface | RouteScheduleSegmentInterface) =>
  segment.mode === MODE_TRANSFER && 'transferType' in segment && segment.transferType === TRANSFER_STOP;

// Determines if Segment is Stop
const getIsRfs = (segment: RouteSegmentInterface | RouteScheduleSegmentInterface) =>
  'vehicleId' in segment && segment.vehicleId === 'iata RFS';

const checkIsShortTransfer = (duration: number, mode: SegmentModeType) => {
  const oceanWindow = 5 * 24 * 60 * 60; // 5 days
  const airWindow = 90 * 60; // 90 mins
  const transferWindow = mode === MODE_WATER ? oceanWindow : airWindow;
  return duration < transferWindow;
};

const getSegmentHasDelay = (segment: RouteSegmentInterface | RouteScheduleSegmentInterface): boolean => {
  return 'attributes' in segment && segment.attributes.vehicleDelay?.status === DELAY_STATUS.DELAYED;
};

interface Props {
  id: string;
  mode: SegmentModeType;
  segments: RouteSegmentInterface[] | RouteScheduleSegmentInterface[];
  segmentLabels?: string[];
  percent?: number;
  alternatives?: number;
  totalDuration?: number;
  passThroughContents?: boolean;
  showWarnings?: boolean;
}

const RouteBarSequence = ({
  id,
  mode,
  segments,
  segmentLabels = [],
  percent = 100,
  alternatives = 0,
  totalDuration = 0,
  passThroughContents = true,
  showWarnings = false,
}: Props) => {
  const setActiveMapSegment = useSetRecoilState(activeMapSegmentState);
  const segmentLen = segments.length - 1;
  const sequencePercent = `${percent}%`;

  const onMouseEnter = (segmentId: string) => {
    setActiveMapSegment(segmentId);
  };

  const onMouseLeave = () => {
    setActiveMapSegment(null);
  };

  return (
    <Sequence key={id} width={sequencePercent} passThroughContents={passThroughContents}>
      {segments.map((segment, index) => {
        const previousSegment = index > 0 ? segments[index - 1] : null;
        const nextSegment = index < segments.length ? segments[index + 1] : null;
        const isTransfer = segment.mode === MODE_TRANSFER;
        const isSegmentStop = checkIfSegmentStop(segment);
        const isSegmentTransfer = checkIfSegmentTransfer(segment);
        const previousWasTransfer = previousSegment && checkIfSegmentTransfer(previousSegment);
        const nextIsTransfer = nextSegment && checkIfSegmentTransfer(nextSegment);
        const stopCount = segment.stops?.length ? segment.stops.length : 0;
        const shouldShowIcon = !isTransfer && (previousWasTransfer || index === 0);
        // Short transfer warning, only show if turned on and if isTransfer
        const isShortTransfer = showWarnings && isSegmentTransfer && checkIsShortTransfer(segment.duration, mode);
        const isRFS = getIsRfs(segment);

        // Calc the percent if a total is provided, else use pre-computed
        const segmentPercent = totalDuration ? (segment.duration / totalDuration) * 100 : segment.percent;
        const segmentPercentStr = `${segmentPercent}%`;

        // Delays
        const hasDelay = getSegmentHasDelay(segment);

        // id to send to Map
        const segmentIdentifier = isTransfer ? segment.from?.nodeReference : segment.id;

        return (
          <Tooltip
            key={segment.id}
            content={<RouteTooltip segment={segment} isShortTransfer={isShortTransfer} />}
            followCursor="horizontal"
          >
            <Segment
              variant={mode}
              isSequenceStart={shouldShowIcon}
              isSequenceEnd={nextIsTransfer}
              isStop={isSegmentStop}
              isTransfer={isSegmentTransfer}
              width={segmentPercentStr}
              hasWarning={isShortTransfer}
              data-testid="segment-bar"
              onMouseEnter={() => segmentIdentifier && onMouseEnter(segmentIdentifier)}
              onMouseLeave={onMouseLeave}
            >
              {hasDelay && shouldShowIcon && (
                <DelayBadge>
                  <TrafficLight color="red" showAlertIcon />
                </DelayBadge>
              )}
              {alternatives > 1 && index === segmentLen && <AltBadge>{alternatives}</AltBadge>}
              {shouldShowIcon && <StyledIcon name={isRFS ? MODE_ROAD : convertModeType(mode)} className="mode-icon" />}
              {segmentLabels && index === 0 && <SegmentLabel>{segmentLabels.join(' / ')}</SegmentLabel>}
              {stopCount > 0 && <StopIndicator />}
            </Segment>
          </Tooltip>
        );
      })}
    </Sequence>
  );
};

const Sequence = styled(Flex)`
  align-items: center;

  /* Uses display: contents to allow all percent sequences to compress well together in Route Results */
  /* Turned off for Schedules as it interferes */
  ${({ passThroughContents }) =>
    passThroughContents &&
    css`
      display: contents;
    `}
`;

const StyledIcon = styled(Icon)`
  height: 20px;
  width: 20px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  position: absolute;
  top: 0;
  left: 0;

  svg {
    height: 14px;
  }
`;

interface SegmentProps {
  readonly variant: SegmentModeType;
  readonly isStop?: boolean;
  readonly isTransfer?: boolean;
  readonly isSequenceStart?: boolean;
  readonly isSequenceEnd?: boolean;
  readonly hasWarning?: boolean;
}

const Segment = styled(Box)<SegmentProps>`
  height: 20px;
  min-width: ${themeGet('space.xs')};
  position: relative;
  background-size: 4px;
  background-repeat: repeat;
  position: relative;
  z-index: 2;

  &:first-child {
    min-width: ${themeGet('space.lg')};
  }

  &:last-child {
    min-width: 10px;
  }

  ${({ isSequenceStart }) =>
    isSequenceStart &&
    css`
      min-width: ${themeGet('space.5')};
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    `};

  ${({ isSequenceEnd }) =>
    isSequenceEnd &&
    css`
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    `};

  ${({ isStop }) =>
    isStop &&
    css`
      min-width: ${themeGet('space.base')};

      &:after {
        content: '';
        position: absolute;
        top: 50%;
        left: 4px;
        right: 4px;
        margin: -3px 0 0 -0;
        height: 6px;
        background: rgba(255, 255, 255, 0.7);
        border-radius: 3px;
      }

      &:hover {
        &:after {
          background: rgba(255, 255, 255, 1);
        }
      }
    `};

  ${({ isTransfer, hasWarning }) =>
    isTransfer &&
    css`
      border-radius: 0px;
      min-width: 1px;
      z-index: 1;

      &:after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: -10px;
        right: -10px;
        background-color: ${themeGet('colors.grey.300')};
        background-size: 4px;
        background-repeat: repeat;
        background-image: url(${DATA_IMAGES.STRIPED_LIGHT});

        ${hasWarning && `background-color: #f7c4c4;`}
      }

      ${media.md} {
        min-width: ${themeGet('space.xs')};
      }
    `};

  &:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  &:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  ${segmentModeVariants};
`;

const AltBadge = styled(Flex)`
  position: absolute;
  align-items: center;
  justify-content: center;
  top: -6px;
  right: -4px;
  background: ${themeGet('colors.yellow')};
  color: ${themeGet('colors.text.primary')};
  z-index: ${themeGet('zIndices.5')};
  font-size: ${themeGet('fontSizes.xxs')};
  min-width: 12px;
  height: 12px;
  border-radius: 5px;
  text-align: center;
  line-height: 10px;
  padding: 0 2px;
`;

const SegmentLabel = styled.span`
  position: absolute;
  bottom: -20px;
  left: ${themeGet('space.xs')};
  font-size: ${themeGet('fontSizes.xs')};
  white-space: nowrap;
  background: white;
`;

const DelayBadge = styled.div`
  position: absolute;
  top: -2px;
  left: 14px;
  z-index: ${themeGet('zIndices.5')};
`;

const StopIndicator = styled.span`
  width: 12px;
  height: 6px;
  display: block;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 3px;
  position: absolute;
  top: 50%;
  left: 50%;
  margin: -3px 0 0 -3px;
`;

export { RouteBarSequence };
